import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { 
  Upload, RefreshCw, Trash2, Search, FolderOpen, Image as ImageIcon, 
  FileText, Film, Grid, List, Eye, Copy, Check, X, Filter
} from 'lucide-react';
import api from '../../lib/axios';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';

const FOLDERS = [
  { id: 'all', name: 'Tous', icon: FolderOpen },
  { id: 'team', name: 'Équipe', icon: ImageIcon },
  { id: 'portfolio', name: 'Réalisations', icon: ImageIcon },
  { id: 'partners', name: 'Partenaires', icon: ImageIcon },
  { id: 'blog', name: 'Blog', icon: FileText },
  { id: 'logos', name: 'Logos', icon: ImageIcon },
  { id: 'cms', name: 'Autres', icon: FolderOpen }
];

const CMSMediaPage = () => {
  const [media, setMedia] = useState([]);
  const [folders, setFolders] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(null);

  const loadMedia = useCallback(async () => {
    try {
      const params = selectedFolder !== 'all' ? `?folder=${selectedFolder}` : '';
      const res = await api.get(`/cms/media${params}`);
      setMedia(res.data.media || []);
      setFolders(res.data.folders || {});
    } catch (error) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedFolder]);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    
    setUploading(true);
    let successCount = 0;
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('folder', selectedFolder === 'all' ? 'cms' : selectedFolder);
      formData.append('convert_webp', 'true');
      
      try {
        await api.post('/cms/upload', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        successCount++;
      } catch (error) {
        toast.error(`Erreur: ${file.name}`);
      }
    }
    
    if (successCount > 0) {
      toast.success(`${successCount} fichier(s) uploadé(s)`);
      loadMedia();
    }
    setUploading(false);
    e.target.value = '';
  };

  const handleDelete = async (mediaItem) => {
    if (!window.confirm('Supprimer ce fichier ?')) return;
    try {
      await api.delete(`/cms/media/${mediaItem.id}`);
      toast.success('Fichier supprimé');
      loadMedia();
      setSelectedMedia(null);
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const copyUrl = (url) => {
    // Fix URLs from old domains
    let fullUrl = url;
    if (url.includes('/api/uploads/')) {
      const match = url.match(/\/api\/uploads\/[^\s"']+/);
      if (match) fullUrl = `${window.location.origin}${match[0]}`;
    } else if (!url.startsWith('http')) {
      fullUrl = `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
    }
    navigator.clipboard.writeText(fullUrl);
    setCopiedUrl(url);
    toast.success('URL copiée !');
    setTimeout(() => setCopiedUrl(null), 2000);
  };

  const [syncing, setSyncing] = useState(false);
  
  const handleSyncMedia = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/cms/media/sync');
      toast.success(res.data.message || 'Synchronisation terminée');
      loadMedia();
    } catch (error) {
      toast.error('Erreur de synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  // Fix URLs - convert absolute URLs to relative
  const [fixingUrls, setFixingUrls] = useState(false);
  const handleFixUrls = async () => {
    setFixingUrls(true);
    try {
      const res = await api.post('/media/fix-urls');
      toast.success(`${res.data.total_fixes || 0} URLs corrigées !`);
      loadMedia();
    } catch (error) {
      toast.error('Erreur lors de la correction des URLs');
    } finally {
      setFixingUrls(false);
    }
  };

  // Migrate local files to MongoDB
  const [migrating, setMigrating] = useState(false);
  const handleMigrateToMongo = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/media/migrate-local-files');
      toast.success(`${res.data.migrated_count || 0} fichiers migrés vers MongoDB !`);
    } catch (error) {
      toast.error('Erreur lors de la migration');
    } finally {
      setMigrating(false);
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const filteredMedia = media.filter(m => 
    !searchQuery || 
    m.original_filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.filename?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalSize = Object.values(folders).reduce((acc, f) => acc + (f.size || 0), 0);
  const totalCount = Object.values(folders).reduce((acc, f) => acc + (f.count || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Médiathèque</h1>
          <p className="text-slate-500">
            {totalCount} fichiers • {formatSize(totalSize)} • Conversion WebP automatique
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Bouton pour corriger les URLs */}
          <Button
            variant="outline"
            onClick={handleFixUrls}
            disabled={fixingUrls}
            className="flex items-center gap-2 border-orange-300 text-orange-600 hover:bg-orange-50"
          >
            {fixingUrls ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {fixingUrls ? 'Correction...' : '🔧 Réparer URLs'}
          </Button>
          
          {/* Bouton pour migrer vers MongoDB */}
          <Button
            variant="outline"
            onClick={handleMigrateToMongo}
            disabled={migrating}
            className="flex items-center gap-2 border-green-300 text-green-600 hover:bg-green-50"
          >
            {migrating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {migrating ? 'Migration...' : '💾 Sauvegarder en BD'}
          </Button>
          
          <Button
            variant="outline"
            onClick={handleSyncMedia}
            disabled={syncing}
            className="flex items-center gap-2"
          >
            {syncing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {syncing ? 'Synchronisation...' : 'Synchroniser'}
          </Button>
          <label className="cursor-pointer">
            <input 
              type="file" 
              className="hidden" 
              accept="image/*,video/*,.pdf" 
              multiple 
              onChange={handleUpload}
              disabled={uploading}
            />
            <div className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors",
              uploading 
                ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                : "bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:from-cyan-600 hover:to-blue-700"
            )}>
              {uploading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Upload...' : 'Uploader'}
            </div>
          </label>
        </div>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Sidebar - Folders */}
        <Card className="lg:col-span-1">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Filter className="w-4 h-4" /> Dossiers
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {FOLDERS.map(folder => {
              const count = folder.id === 'all' ? totalCount : (folders[folder.id]?.count || 0);
              return (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.id)}
                  className={cn(
                    "w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors",
                    selectedFolder === folder.id
                      ? "bg-cyan-50 text-cyan-700 font-medium"
                      : "hover:bg-slate-50 text-slate-600"
                  )}
                >
                  <span className="flex items-center gap-2">
                    <folder.icon className="w-4 h-4" />
                    {folder.name}
                  </span>
                  <Badge variant="secondary" className="text-xs">{count}</Badge>
                </button>
              );
            })}
          </CardContent>
        </Card>

        {/* Main Content */}
        <div className="lg:col-span-4 space-y-4">
          {/* Toolbar */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher un fichier..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex border rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={cn("p-2 transition-colors", viewMode === 'grid' ? "bg-cyan-50 text-cyan-600" : "hover:bg-slate-50")}
              >
                <Grid className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={cn("p-2 transition-colors", viewMode === 'list' ? "bg-cyan-50 text-cyan-600" : "hover:bg-slate-50")}
              >
                <List className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Media Grid/List */}
          {filteredMedia.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-slate-500">
                <ImageIcon className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>Aucun fichier dans ce dossier</p>
                <p className="text-sm mt-1">Uploadez des images pour commencer</p>
              </CardContent>
            </Card>
          ) : viewMode === 'grid' ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredMedia.map(item => {
                // Fix URLs from old domains
                let imageUrl = item.url;
                if (item.url?.includes('/api/uploads/')) {
                  const match = item.url.match(/\/api\/uploads\/[^\s"']+/);
                  if (match) imageUrl = `${window.location.origin}${match[0]}`;
                } else if (!item.url?.startsWith('http')) {
                  imageUrl = `${window.location.origin}${item.url?.startsWith('/') ? '' : '/'}${item.url}`;
                }
                return (
                <div
                  key={item.id}
                  className="group relative aspect-square bg-slate-100 rounded-lg overflow-hidden cursor-pointer border hover:border-cyan-500 transition-colors"
                  onClick={() => setSelectedMedia(item)}
                >
                  {item.mime_type?.startsWith('image') ? (
                    <img
                      src={imageUrl}
                      alt={item.title || item.original_filename}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect fill="%23f1f5f9" width="100" height="100"/><text x="50" y="55" text-anchor="middle" fill="%2394a3b8" font-size="12">Image non disponible</text></svg>';
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <FileText className="w-12 h-12 text-slate-300" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    <button 
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      onClick={(e) => { e.stopPropagation(); setSelectedMedia(item); }}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <button 
                      className="p-2 bg-white rounded-full hover:bg-slate-100"
                      onClick={(e) => { e.stopPropagation(); copyUrl(item.url); }}
                    >
                      {copiedUrl === item.url ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                    </button>
                    <button 
                      className="p-2 bg-red-500 rounded-full hover:bg-red-600"
                      onClick={(e) => { e.stopPropagation(); handleDelete(item); }}
                    >
                      <Trash2 className="w-4 h-4 text-white" />
                    </button>
                  </div>
                  {item.type === 'external' && (
                    <Badge className="absolute top-2 left-2 bg-blue-500 text-white text-xs">Externe</Badge>
                  )}
                  {item.mime_type === 'image/webp' && (
                    <Badge className="absolute top-2 right-2 bg-green-500 text-white text-xs">WebP</Badge>
                  )}
                </div>
              )})}
            </div>
          ) : (
            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Fichier</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Taille</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Format</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredMedia.map(item => {
                      // Fix URLs from old domains
                      let imageUrl = item.url;
                      if (item.url?.includes('/api/uploads/')) {
                        const match = item.url.match(/\/api\/uploads\/[^\s"']+/);
                        if (match) imageUrl = `${window.location.origin}${match[0]}`;
                      } else if (!item.url?.startsWith('http')) {
                        imageUrl = `${window.location.origin}${item.url?.startsWith('/') ? '' : '/'}${item.url}`;
                      }
                      return (
                      <tr key={item.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-100 rounded overflow-hidden flex-shrink-0">
                              {item.mime_type?.startsWith('image') ? (
                                <img 
                                  src={imageUrl} 
                                  alt="" 
                                  className="w-full h-full object-cover" 
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              ) : (
                                <FileText className="w-full h-full p-2 text-slate-300" />
                              )}
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm truncate max-w-[200px]">{item.title || item.original_filename}</span>
                              {item.type === 'external' && <span className="text-xs text-blue-500">Externe</span>}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500">{item.type === 'external' ? '-' : formatSize(item.size)}</td>
                        <td className="px-4 py-3">
                          <Badge variant="secondary" className="text-xs">
                            {item.mime_type?.split('/')[1]?.toUpperCase() || 'FILE'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setSelectedMedia(item)}>
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => copyUrl(item.url)}>
                              {copiedUrl === item.url ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(item)}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Detail Modal */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <span className="truncate pr-4">{selectedMedia?.original_filename}</span>
              {selectedMedia?.mime_type === 'image/webp' && (
                <Badge className="bg-green-500 text-white">WebP optimisé</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="space-y-4">
              {/* Preview */}
              <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center">
                {selectedMedia.mime_type?.startsWith('image') ? (
                  <img
                    src={(() => {
                      const url = selectedMedia.url;
                      if (url?.includes('/api/uploads/')) {
                        const match = url.match(/\/api\/uploads\/[^\s"']+/);
                        if (match) return `${window.location.origin}${match[0]}`;
                      }
                      if (url?.startsWith('http')) return url;
                      return `${window.location.origin}${url?.startsWith('/') ? '' : '/'}${url}`;
                    })()}
                    alt={selectedMedia.original_filename}
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <FileText className="w-24 h-24 text-slate-300" />
                )}
              </div>
              
              {/* Info */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-slate-500">Taille</Label>
                  <p className="font-medium">{formatSize(selectedMedia.size)}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Dimensions</Label>
                  <p className="font-medium">
                    {selectedMedia.width && selectedMedia.height 
                      ? `${selectedMedia.width} × ${selectedMedia.height} px`
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <Label className="text-slate-500">Format</Label>
                  <p className="font-medium">{selectedMedia.mime_type}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Dossier</Label>
                  <p className="font-medium">{selectedMedia.folder}</p>
                </div>
              </div>
              
              {/* URL */}
              <div>
                <Label className="text-slate-500">URL</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input 
                    value={(() => {
                      const url = selectedMedia.url;
                      if (url?.includes('/api/uploads/')) {
                        const match = url.match(/\/api\/uploads\/[^\s"']+/);
                        if (match) return `${window.location.origin}${match[0]}`;
                      }
                      if (url?.startsWith('http')) return url;
                      return `${window.location.origin}${url?.startsWith('/') ? '' : '/'}${url}`;
                    })()}
                    readOnly
                    className="text-sm"
                  />
                  <Button onClick={() => copyUrl(selectedMedia.url)}>
                    {copiedUrl === selectedMedia.url ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="destructive" onClick={() => handleDelete(selectedMedia)}>
              <Trash2 className="w-4 h-4 mr-2" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CMSMediaPage;
